/**
 * AnswerPartsList — one row per part of a descriptive answer, as the question
 * labelled it: the mark, the label, the note. `DescriptiveAnswerV1` emits
 * these as `answerParts`. A part is something the question asked, not a step
 * the student took — there is no order of working and no first-break, so this
 * is not `StepsList`. Stateless atom.
 */
import type { AnswerPart } from '@padcanvas/shared';
import { LatexText } from '../../latex';

interface Props {
    parts: readonly AnswerPart[] | undefined;
    title?: string;
}

const MARK: Record<AnswerPart['result'], string> = { right: '✓', partly: '◐', wrong: '✕', missing: '—' };
const FALLBACK_NOTE: Partial<Record<AnswerPart['result'], string>> = { right: 'Right', missing: 'Not answered' };

export function AnswerPartsList({ parts, title = 'Part by part' }: Props) {
    if (!parts || parts.length === 0) return null;
    return (
        <section className="sol-section sol-answer-parts">
            <h3 className="sol-section-title">{title}</h3>
            <ul className="sol-answer-parts-list">
                {parts.map(part => (
                    <li key={part.label} className={`sol-answer-part sol-answer-part--${part.result}`}>
                        <span className="sol-answer-part-mark" aria-hidden="true">{MARK[part.result]}</span>
                        <span className="sol-answer-part-label">{part.label}</span>
                        <span className="sol-answer-part-note">
                            {part.note ? <LatexText text={part.note} /> : (FALLBACK_NOTE[part.result] ?? '')}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
